import { XMark } from '@/public/icons/tsx';
import Image from 'next/image';
import { MouseEvent, ReactNode } from 'react';
import { Bounce, toast, ToastPosition, ToastTransition } from 'react-toastify';

interface ToastCloseButtonProps {
	closeToast: (e: MouseEvent<HTMLElement>) => void;
}

// CLAIM-3492 — the close button must be a real, focusable, labeled control
// (WCAG 2.1.1 Keyboard, 4.1.2 Name Role Value); a bare icon isn't operable
// by keyboard and isn't announced to screen readers.
const ToastCloseButton = ({ closeToast }: ToastCloseButtonProps) => (
	<button type="button" aria-label="Close notification" onClick={closeToast}>
		<XMark className="mt-2" />
	</button>
);

export const useNotification = () => {
	const toastSuccess = (
		message: string | ReactNode,
		position: ToastPosition = 'bottom-right',
		transition: ToastTransition = Bounce
	) => {
		safeDismiss();
		return toast.success(message, {
			position: position,
			autoClose: 3000,
			hideProgressBar: true,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			transition: transition,
			icon: () => (
				<Image
					src="/icons/svg/check-circle.svg"
					alt=""
					width={28}
					height={28}
					unoptimized
				/>
			),
			closeButton: ToastCloseButton,
			style: { boxShadow: '0px 2px 5px 0px #0000003D', width: 'fit-content' },
		});
	};

	const toastInfo = (
		message: string | ReactNode,
		position: ToastPosition = 'bottom-right',
		transition: ToastTransition = Bounce
	) => {
		safeDismiss();
		return toast.info(message, {
			position: position,
			autoClose: 3000,
			hideProgressBar: true,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			transition: transition,
			icon: () => (
				<Image
					src="/icons/svg/info-circle-blue.svg"
					alt=""
					width={28}
					height={28}
					unoptimized
				/>
			),
			closeButton: ToastCloseButton,
			style: { boxShadow: '0px 2px 5px 0px #0000003D' },
		});
	};

	const toastError = (
		message: string | ReactNode,
		position: ToastPosition = 'bottom-right',
		transition: ToastTransition = Bounce
	) => {
		safeDismiss();
		return toast.error(message, {
			position: position,
			autoClose: 3000,
			hideProgressBar: true,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			transition: transition,
			icon: () => (
				<svg
					width="28"
					height="28"
					viewBox="0 0 40 41"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M19.9997 38.7035C10.1163 38.7035 2.08301 30.6701 2.08301 20.7868C2.08301 10.9035 10.1163 2.87012 19.9997 2.87012C29.883 2.87012 37.9163 10.9035 37.9163 20.7868C37.9163 30.6701 29.883 38.7035 19.9997 38.7035ZM19.9997 5.37012C11.4997 5.37012 4.58301 12.2868 4.58301 20.7868C4.58301 29.2868 11.4997 36.2035 19.9997 36.2035C28.4997 36.2035 35.4163 29.2868 35.4163 20.7868C35.4163 12.2868 28.4997 5.37012 19.9997 5.37012Z"
						fill="#FF0043"
					/>
					<path
						d="M15.2829 26.7536C14.9663 26.7536 14.6496 26.637 14.3996 26.387C13.9163 25.9036 13.9163 25.1036 14.3996 24.6203L23.8329 15.187C24.3163 14.7036 25.1163 14.7036 25.5996 15.187C26.0829 15.6703 26.0829 16.4703 25.5996 16.9536L16.1663 26.387C15.9329 26.637 15.5996 26.7536 15.2829 26.7536Z"
						fill="#FF0043"
					/>
					<path
						d="M24.7163 26.7536C24.3996 26.7536 24.0829 26.637 23.8329 26.387L14.3996 16.9536C13.9163 16.4703 13.9163 15.6703 14.3996 15.187C14.8829 14.7036 15.6829 14.7036 16.1663 15.187L25.5996 24.6203C26.0829 25.1036 26.0829 25.9036 25.5996 26.387C25.3496 26.637 25.0329 26.7536 24.7163 26.7536Z"
						fill="#FF0043"
					/>
				</svg>
			),
			closeButton: ToastCloseButton,
			style: { boxShadow: '0px 2px 5px 0px #0000003D' },
		});
	};

	const safeDismiss = () => {
		try {
			toast.dismiss();
		} catch (error) {
			console.warn('Error dismissing toast', error);
		}
	};

	return {
		toastSuccess,
		toastError,
		toastInfo,
	};
};
